import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { orange } from '@material-ui/core/colors';
import ExpansionSection from 'components/templates/ExpansionSection';
import Api from 'api';
import { showSnackbar } from 'actions';
import * as userSelectors from 'selectors/user';

const ProxyConsentBlock = ({ disabled }) => {
  const dispatch = useDispatch();
  const user = useSelector(userSelectors.userData);
  const showProxyConsent =
    user?.kyc_status === 'SUCCEDED' ||
    user?.kyc_status === 'SUCCEEDED' ||
    !!(user?.iban && user.iban.trim());

  const [proxyManagementLoading, setProxyManagementLoading] = React.useState(false);
  const [showProxyButton, setShowProxyButton] = React.useState(false);
  const [checkingProxyStatus, setCheckingProxyStatus] = React.useState(true);
  const [expanded, setExpanded] = React.useState(true);
  const mountedRef = React.useRef(true);

  // Check if user should see proxy management button
  React.useEffect(() => {
    mountedRef.current = true;

    const checkProxyStatus = async () => {
      try {
        if (mountedRef.current) {
          setShowProxyButton(showProxyConsent);
        }
      } catch (error) {
        if (mountedRef.current) {
          setShowProxyButton(false);
        }
      } finally {
        if (mountedRef.current) {
          setCheckingProxyStatus(false);
        }
      }
    };

    checkProxyStatus();

    return () => {
      mountedRef.current = false;
    };
  }, [showProxyConsent]);

  const handleProxyManagement = async () => {
    setProxyManagementLoading(true);
    try {
      const response = await Api.manageProxyConsent();

      if (!response) {
        setProxyManagementLoading(false);
        dispatch(
          showSnackbar({
            severity: 'info',
            message: {
              id: 'mangopay.proxy.notRequired',
              defaultMessage: 'Proxy consent is not required for your account',
            },
          }),
        );
        return;
      }

      const redirectUrl = response?.redirectUrl;

      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        setProxyManagementLoading(false);
        dispatch(
          showSnackbar({
            severity: 'info',
            message: {
              id: 'mangopay.proxy.notRequired',
              defaultMessage: 'Proxy consent is not required for your account',
            },
          }),
        );
      }
    } catch (error) {
      setProxyManagementLoading(false);
      // Error handling: Consider adding onError prop or using snackbar via container
      // For now, errors will be handled by the API interceptor (401 auto-logout)
    }
  };

  if (checkingProxyStatus || !showProxyButton) {
    return null;
  }

  return (
    <ExpansionSection
      expanded={expanded}
      summary={
        <FormattedMessage
          id="mangopay.proxy.consent.title"
          defaultMessage="Mangopay Consent - settings"
        />
      }
      onChange={() => setExpanded(!expanded)}
    >
      <Box>
        <Typography variant="body2" style={{ marginBottom: 16 }}>
          <FormattedMessage
            id="mangopay.proxy.consent.info"
            defaultMessage="To receive payouts, you need to accept proxy consent at Mangopay. This allows authorized actions to be taken on your behalf for payment processing."
          />
        </Typography>
        <Button
          variant="contained"
          fullWidth
          onClick={handleProxyManagement}
          disabled={proxyManagementLoading || disabled}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: orange[300],
            color: '#fff',
          }}
        >
          {proxyManagementLoading && (
            <CircularProgress size={20} style={{ marginRight: 8, color: '#fff' }} />
          )}
          <FormattedMessage id="mangopay.proxy.management" defaultMessage="Mangopay Consent" />
        </Button>
      </Box>
    </ExpansionSection>
  );
};

ProxyConsentBlock.propTypes = {
  disabled: PropTypes.bool,
};

ProxyConsentBlock.defaultProps = {
  disabled: false,
};

export default ProxyConsentBlock;
